import { debounce, isEqual, map, omit } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { defaultMemoize } from 'reselect'

import Card from '@/components/Card'
import { Person } from '@/components/Icons'
import PaginationTable from '@/components/PaginationTable'

import AuditFilters from './AuditFilters'
import columns from './columns'
import messages from './messages'

import './index.scss'

import { usePrevious } from 'react-use'

function AuditView(props: any) {
  const intl = useIntl()

  const [state, _setState] = useState({
    searchParams: {
      oldest: moment(new Date()).add(-1, 'days').format('YYYY-MM-DD HH:mm').toString(),
      latest: moment(new Date()).format('YYYY-MM-DD HH:mm').toString(),
      message: ['']
    },
    query: null,
    openSections: [],
    sortingColumns: null, // reference to the sorting columns
    message: <FormattedMessage {...messages.fetchingLogs} />,
    currentPage: 1
  })
  const setState = (update: Partial<Parameters<typeof _setState>[0]>) => {
    return _setState(state => ({ ...state, ...(typeof update === 'function' ? update(state) : update) }))
  }

  // componentDidMount
  useEffect(() => {
    props.searchLogs(state.searchParams)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // componentDidUpdate
  const prevState = usePrevious(state)
  useEffect(() => {
    if (!prevState) return

    // We want to reset the offset and current page only when fields have changed
    if (haveFiltersBeenChanged(prevState, state)) {
      setState({
        searchParams: {
          ...state.searchParams,
          offset: 0
        },
        // @ts-expect-error
        query: state.query ? { ...state.query, offset: 0 } : null,
        currentPage: 1
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevState, state])

  const haveFiltersBeenChanged = (prevState: any, currentState: any) => {
    if (!prevState.query && !currentState.query) {
      return false
    } else if (Boolean(prevState.query) && !currentState.query) {
      return false
    } else if (!isEqual(omit(prevState.query, ['offset']), omit(currentState.query, ['offset']))) {
      return true
    } else if (
      !isEqual(prevState.searchParams.oldest, state.searchParams.oldest) ||
      !isEqual(prevState.searchParams.latest, state.searchParams.latest)
    ) {
      return true
    } else {
      return false
    }
  }

  const onSelect = (page: any) => {
    const offset = (page - 1) * props.perPage
    setState({
      message: <FormattedMessage {...messages.fetchingLogs} />,
      searchParams: {
        ...state.searchParams,
        offset
      },
      // @ts-expect-error
      query: state.query ? { ...state.query, offset } : null,
      openSections: [],
      currentPage: page
    })

    //@ts-expect-error
    searchLogs(state.query ? { ...state.query, offset } : { ...state.searchParams, offset })
  }
  const debounceUserSearch = debounce(filter => {
    props.searchUsers(filter)
  }, 300)

  const generateSearchResults = defaultMemoize((searchTerm, users) => {
    if (searchTerm.length === 0) {
      return []
    }

    return map(users, user => ({
      id: user.id,
      label: user.displayName,
      leftIcon: <Person />,
      subLabel: user.email,
      value: user.email
    }))
  })

  const searchLogs = (searchParams: any, query?: any) => {
    if (query && query?.query) {
      query.query = query.query.replaceAll('BUILDER', 'WORKSHOP')
    }

    setState({
      searchParams: searchParams,
      query: query
    })
    props.searchLogs(query || searchParams)
  }

  const onAccordionClicked = (e: any, titleProps: any) => {
    const { index } = titleProps
    const modifiedSections = state.openSections.slice()

    // @ts-expect-error
    modifiedSections.includes(index)
      ? // @ts-expect-error
        modifiedSections.splice(modifiedSections.indexOf(index), 1)
      : // @ts-expect-error
        modifiedSections.push(index)

    setState({ openSections: modifiedSections })
  }

  const BodyWrapper = (props: any) => <tbody {...props} />
  BodyWrapper.shouldComponentUpdate = () => {
    return true
  }
  const RowWrapper = (props: any) => <tr {...props} />
  RowWrapper.shouldComponentUpdate = () => {
    return true
  }

  const events = props.events.filter((event: any) => {
    event.key = event.key.replace('WORKSHOP', 'BUILDER')
    return true
  })
  const sortedEvents = events.sort((a: any, b: any) => a.key.localeCompare(b.key))

  const searchParams = state.searchParams
  if (searchParams?.message) {
    const searchMessageParams = state.searchParams?.message.map((search: any) => {
      search = search.replace('BUILDER', 'WORKSHOP')
      return search
    })
    searchParams.message = searchMessageParams
  }

  return (
    <Card isActionInProgress={props.isActionInProgress} actionMessage={state.message}>
      <Card.Header title={<FormattedMessage {...messages.audit} />} />
      <Card.Content>
        <div className="audit-view-wrapper">
          <AuditFilters
            isSearching={props.isSearching}
            searchParams={searchParams}
            searchLogs={searchLogs}
            users={props.users}
            fetchUserSearchResults={debounceUserSearch}
            generateSearchResults={generateSearchResults}
            events={sortedEvents || []}
          />
          {props.logs.length > 0 ? (
            <PaginationTable
              columns={columns(
                onAccordionClicked,
                () => {
                  return state.openSections
                },
                intl
              )}
              renderers={{
                body: {
                  wrapper: BodyWrapper,
                  row: RowWrapper
                }
              }}
              pagination={{
                count: props.auditTotalCount,
                startIndex: state.currentPage * props.perPage,
                perPage: props.perPage
              }}
              onPaginateSelect={onSelect}
              rows={props.logs}
              rowItemType={<FormattedMessage {...messages.audit} />}
              rowKey="_index"
              sortingColumn={props.sortingColumn}
            />
          ) : (
            <p className="no-users flex-center">
              <FormattedMessage {...messages.noLogs} />
            </p>
          )}
        </div>
      </Card.Content>
    </Card>
  )
}

AuditView.displayName = 'AuditView'

AuditView.propTypes = {
  isActionInProgress: PropTypes.bool,

  auditTotalCount: PropTypes.number.isRequired,
  offset: PropTypes.number.isRequired,
  searchLogs: PropTypes.func.isRequired,
  searchUsers: PropTypes.func.isRequired,
  perPage: PropTypes.number.isRequired,
  isSearching: PropTypes.bool,
  users: PropTypes.arrayOf(PropTypes.object).isRequired,
  logs: PropTypes.arrayOf(PropTypes.object).isRequired,
  events: PropTypes.arrayOf(PropTypes.object)
}

AuditView.defaultProps = {
  isSearching: false,
  isActionInProgress: false,
  events: []
}

export default AuditView
